import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { EditIcon, PlusIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Field, FieldContent, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { Employee, Location } from '../data/databaseSchema';
import { type EmployeeLocationModelsDetail, employeeService } from '../services/employeeService';
import { locationService } from '../services/locationService';

const CREATE_NEW_LOCATION_VALUE = '__create_new_location__';

interface EmployeeLocationModelFormDrawerProps {
  employee: Employee | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  entry?: EmployeeLocationModelsDetail | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
  isDeleteDisabled?: boolean;
}

export const EmployeeLocationModelFormDrawer: React.FC<EmployeeLocationModelFormDrawerProps> = ({
  employee,
  isOpen,
  onClose,
  onSave,
  entry,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
  isDeleteDisabled = false,
}) => {
  const [formData, setFormData] = useState({
    location_id: '',
    start_date: '',
    end_date: '',
    no_end_date: false,
    status: 'active',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCreateNewLocation = () => {
    onClose();
    navigate({ to: '/settings/locations' });
  };

  const { data: locationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationService.getLocations(),
    enabled: isOpen,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const locationOptions = useMemo(() => {
    if (!locationsData?.length) return [];
    return locationsData.map((location: Location) => ({
      value: location.id.toString(),
      label: location.name,
    }));
  }, [locationsData]);

  const selectedLocationLabel = useMemo(() => {
    const match = locationOptions.find((opt) => opt.value === formData.location_id);
    if (match) return match.label;
    if (formData.location_id && (isEdit || readOnly) && entry?.location?.name) {
      return entry.location.name;
    }
    return undefined;
  }, [locationOptions, formData.location_id, isEdit, readOnly, entry]);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && entry) {
        const startDate = entry.start_date ? new Date(entry.start_date) : null;
        const endDate = entry.end_date ? new Date(entry.end_date) : null;
        setFormData({
          location_id: entry.location_id || '',
          start_date: startDate ? startDate.toISOString().split('T')[0] : '',
          end_date: endDate ? endDate.toISOString().split('T')[0] : '',
          no_end_date: entry.no_end_date || false,
          status: entry.status || 'active',
        });
      } else {
        setFormData({
          location_id: '',
          start_date: '',
          end_date: '',
          no_end_date: false,
          status: 'active',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, entry]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.location_id) {
      newErrors.location_id = 'Location is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start Date is required';
    }
    if (!formData.no_end_date) {
      if (!formData.end_date) {
        newErrors.end_date = 'End Date is required';
      }
      if (
        formData.start_date &&
        formData.end_date &&
        new Date(formData.start_date) > new Date(formData.end_date)
      ) {
        newErrors.end_date = 'End Date must be after Start Date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !employee?.employee_id) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData: {
        location_id: string;
        start_date: string;
        end_date?: string;
        no_end_date?: boolean;
        status?: string;
      } = {
        location_id: formData.location_id,
        start_date: new Date(formData.start_date).toISOString(),
        status: formData.status,
        no_end_date: formData.no_end_date,
      };

      if (!formData.no_end_date && formData.end_date) {
        apiData.end_date = new Date(formData.end_date).toISOString();
      }

      if (isEdit && entry) {
        await employeeService.updateEmployeeLocationModel(employee.employee_id, entry.id, apiData);
      } else {
        await employeeService.createEmployeeLocationModel(employee.employee_id, apiData);
      }

      onSave();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        readOnly ? 'View Location Model' : isEdit ? 'Edit Location Model' : 'Add Location Model'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Model' : 'Add Model'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={isDeleteDisabled}
              onClick={onDeleteRequest}
            >
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <Field data-invalid={!!errors.location_id}>
          <FieldLabel>Location *</FieldLabel>
          <FieldContent>
            <Select
              value={formData.location_id}
              onValueChange={(value) => {
                if (value == null) return;
                if (value === CREATE_NEW_LOCATION_VALUE) {
                  handleCreateNewLocation();
                  return;
                }
                setFormData((prev) => ({ ...prev, location_id: value }));
                if (errors.location_id) {
                  setErrors((prev) => ({ ...prev, location_id: '' }));
                }
              }}
              disabled={readOnly || isLoadingLocations}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue
                  placeholder={isLoadingLocations ? 'Loading locations...' : 'Select a location'}
                >
                  {selectedLocationLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent alignItemWithTrigger={false} sideOffset={8}>
                {locationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
                {!readOnly ? (
                  <>
                    <SelectSeparator />
                    <SelectItem value={CREATE_NEW_LOCATION_VALUE} className="text-link">
                      <PlusIcon className="size-4" />
                      Create new location
                    </SelectItem>
                  </>
                ) : null}
              </SelectContent>
            </Select>
            {errors.location_id ? <FieldError>{errors.location_id}</FieldError> : null}
          </FieldContent>
        </Field>
        <TextFormField
          label="Start Date"
          type="date"
          required
          value={formData.start_date}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, start_date: value }));
            if (errors.start_date) {
              setErrors((prev) => ({ ...prev, start_date: '' }));
            }
          }}
          error={errors.start_date}
          disabled={readOnly}
        />
        <Field>
          <div className="flex items-center gap-3">
            <Switch
              id="no-end-date"
              checked={formData.no_end_date}
              disabled={readOnly}
              onCheckedChange={(checked) => {
                const noEndDate = checked === true;
                setFormData((prev) => ({
                  ...prev,
                  no_end_date: noEndDate,
                  end_date: noEndDate ? '' : prev.end_date,
                }));
                if (errors.end_date) {
                  setErrors((prev) => ({ ...prev, end_date: '' }));
                }
              }}
            />
            <Label htmlFor="no-end-date">No End Date</Label>
          </div>
        </Field>
        {!formData.no_end_date ? (
          <TextFormField
            label="End Date"
            type="date"
            required
            value={formData.end_date}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, end_date: value }));
              if (errors.end_date) {
                setErrors((prev) => ({ ...prev, end_date: '' }));
              }
            }}
            error={errors.end_date}
            disabled={readOnly}
          />
        ) : null}
        <SelectFormField
          label="Status"
          value={formData.status}
          onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
          disabled={readOnly}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
